import {
  Color,
  Column,
  IdAndOrder,
  Task as TaskModel,
  TaskReorderParams,
  UpdateFields
} from "@/interfaces/TaskInterfaces";
import Task from "@/componentsReusable/tasks/Task";
import TaskModal from "@/componentsReusable/tasks/modal/TaskModal";
import {reorderTask, updateTask} from "@/services/TaskService";
import {Container, Draggable} from "vue-smooth-dnd";

/**
 * Mixin to bring in common Task Column Functionality.
 */
export default {
  props: {
    tasks: { type: Array },
    screenStateKey: {type: String},
    columnId: { type: Number, default:0},
    maxRows: {type: Number},
    acceptDrop: {type: Boolean, default: true}
  },
  components: {
    Task,
    TaskModal,
    Container,
    Draggable
  },
  data() {
    return {
      ourTasks: [] as TaskModel[],
      modalTaskId: 0,
      screenState: {
        cardExpanded: true,
        editingTasks: [],
        showDetailsTasks: []
      }
    }
  },
  computed: {
    hasUnread(): boolean {
      return ((this as any).tasks as TaskModel[]).filter(task => !task.isRead).length > 0;
    },
    hasEmergency(): boolean {
      return ((this as any).tasks as TaskModel[]).filter(task => task.isEmergency).length > 0;
    }
  },
  watch: {
    tasks(newTasks: TaskModel[]) {
      (this as any).setTasks(newTasks);
    }
  },
  created() {
    (this as any).setTasks((this as any).tasks as TaskModel[]);
  },
  methods: {
    setTasks(newTasks: TaskModel[]) {
      (this as any).loadEditingTasksState(newTasks);
      // Need to make sure we work with a copy or Vuex gets angry we're making changes without it.
      (this as any).ourTasks = newTasks.map((task: any) => {
        const oldTask = (this as any).ourTasks.find((oldTask: TaskModel) => oldTask.id === task.id);
        const toReturn = Object.assign({}, task);
        toReturn.isEditing = oldTask ? oldTask.isEditing : task.isEditing;
        return toReturn;
      });
    },
    loadEditingTasksState(tasks: TaskModel[]) {
      if((this as any).$store.state.viewState[(this as any).screenStateKey]) {
        (this as any).screenState = JSON.parse(JSON.stringify((this as any).$store.state.viewState[(this as any).screenStateKey]));
        if (!(this as any).screenState.editingTasks) {
          (this as any).screenState.editingTasks = [];
        }
        if (!(this as any).screenState.showDetailsTasks) {
          (this as any).screenState.showDetailsTasks = [];
        }
      }
      const onLoadEditState: number[] = [];
      tasks.forEach((task : TaskModel) => {
        if ((this as any).screenState.editingTasks.indexOf(task.id) !== -1) {
          task.isEditing = true;
          onLoadEditState.push(task.id);
        } else {
          task.isEditing = false;
        }
      });
      const onLoadDetailState: number[] = [];
      tasks.forEach((task : TaskModel) => {
        task.showDetails = false;

        if ((this as any).screenState.showDetailsTasks.indexOf(task.id) !== -1) {
          task.showDetails = true;
          onLoadDetailState.push(task.id);
        }
      });
      // update list with only tasks that we ended up expanding.  Leave the local state clean instead of holding a reference to
      // tasks that may have been handled/reassigned elsewhere
      (this as any).screenState.editingTasks = onLoadEditState;
      (this as any).screenState.showDetailsTasks = onLoadDetailState;
    },
    expandCard() {
      (this as any).screenState.cardExpanded = !(this as any).screenState.cardExpanded;
      (this as any).storeState();
    },
    async toggleEdit(task: TaskModel) {
      task.isEditing = !task.isEditing;
      // Hack to get it to go
      (this as any).ourTasks = (this as any).ourTasks.map((task: any) => task);
      (this as any).storeEditingTaskState(task);
    },
    storeEditingTaskState(task: TaskModel) {
      const taskId = task.id;
      const editingTasks = (this as any).screenState.editingTasks;
      if (task.isEditing && editingTasks.indexOf(taskId) < 0){
        editingTasks.push(taskId);
      } else if (!task.isEditing && editingTasks.indexOf(taskId) >= 0) {
        editingTasks.splice(editingTasks.indexOf(taskId), 1);
      }
      (this as any).storeState();
    },
    storeShowDetailsState(task: TaskModel) {
      const taskId = task.id;
      const editingTasks = (this as any).screenState.showDetailsTasks;
      if (task.showDetails && editingTasks.indexOf(taskId) < 0){
        editingTasks.push(taskId);
      } else if (!task.showDetails && editingTasks.indexOf(taskId) >= 0) {
        editingTasks.splice(editingTasks.indexOf(taskId), 1);
      }
      (this as any).storeState();
    },
    storeState() {
      (this as any).$store.dispatch('viewState/updateViewState',
        {name: (this as any).screenStateKey, value: JSON.parse(JSON.stringify((this as any).screenState))});
    },
    async markRead(task: TaskModel) {
      try {
        await updateTask(task.id, UpdateFields.IS_READ, {newValue: true});
      } catch (err) {
        console.error(err, err && err.response ? JSON.stringify(err.response) : '');
        (this as any).$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'Failed to mark Task as read.'
        });
      }
    },
    /**
     * In case I forget, this is a method and not a filter because it needs to access `this`
     */
    userFullName(appUserId: number): string {
      const user = (this as any).$store.getters['user/getUser'](appUserId);
      if (user) {
        return `${user.firstName} ${user.lastName}`;
      }
      return 'Unknown';
    },
    getColor(task: TaskModel): any {
      return {
        'border-purple': task.taskColorId === Color.PURPLE,
        'border-red': task.taskColorId === Color.RED,
        'border-blue': task.taskColorId === Color.BLUE,
        'border-green': task.taskColorId === Color.GREEN,
        'border-yellow': task.taskColorId === Color.YELLOW
      }
    },
    taskCollapsed(task: TaskModel) {
      task.isEditing = false;
    },
    openTask(modalTaskId: number) {
      (this as any).modalTaskId = modalTaskId;
    },
    getTasksForReorder() : TaskModel[] {
      return (this as any).ourTasks;
    },
    // Drag and Drop functions
    async onDrop(dropResult: any) {
      const { removedIndex, addedIndex, payload } = dropResult;
      if (removedIndex === null && addedIndex === null) {
        return;
      }
      const tasksForReorder : TaskModel[] = (this as any).getTasksForReorder();
      const reorderParams : TaskReorderParams = {
        movedTaskInfo: null,
        newColumn: null,
        oldColumn: null
      };
      let itemToAdd = payload;

      reorderParams.oldColumn = {
        assignedToAppUserId: payload.assignedToAppUserId,
        assignedToSecurityRoleId: payload.assignedToSecurityRoleId,
        columnId: payload.taskColumnId,
        orderings: []
      };

      if (removedIndex !== null) {
        tasksForReorder.splice(removedIndex, 1);
        reorderParams.oldColumn.orderings = tasksForReorder
          .map((task : TaskModel, index : number) => {
            task.displayOrder = index;
            (this as any).$store.dispatch('task/updateTask', task);
            return {taskId: task.id, columnOrder: index};
          });
        reorderParams.oldColumn.columnId = (this as any).columnId;
      }

      if (addedIndex !== null) {
        tasksForReorder.splice(addedIndex, 0, itemToAdd);
        const columnOrders: IdAndOrder[] = tasksForReorder
          .map((task : TaskModel, index : number) => {
            // the server will also update the display order and notify via websocket, but we need to commit the changes to state to prevent flickering
            task.displayOrder = index;
            (this as any).$store.dispatch('task/updateTask', task);
            return {taskId: task.id, columnOrder: index};
          });
        const newAssignee = this.getColumnAssignee();

        reorderParams.newColumn = {
          assignedToAppUserId: newAssignee.assignedToAppUserId,
          assignedToSecurityRoleId: newAssignee.assignedToSecurityRoleId,
          columnId: (this as any).columnId,
          orderings: columnOrders
        };
        const isComplete = (this as any).columnId === Column.COMPLETE;
        reorderParams.movedTaskInfo = {
          isComplete: isComplete,
          taskId: payload.id
        };
        this.updateTaskInVuex(payload, newAssignee, isComplete);
      }
      await this.reorderTask(payload.id, reorderParams);
    },
    updateTaskInVuex(task: TaskModel, assignee: any, isComplete: boolean) {
      // the server will also update the display order and notify via websocket, but we need to commit the changes to state to prevent flickering
      task.taskColumnId = (this as any).columnId;
      task.assignedToAppUserId = assignee.assignedToAppUserId;
      task.assignedToSecurityRoleId = assignee.assignedToSecurityRoleId;
      task.isComplete = isComplete;
      (this as any).$store.dispatch('task/updateTask', task);
    },
    async reorderTask(taskId: number, taskReorderParams: TaskReorderParams): Promise<void> {
      try {
        await reorderTask(taskId, taskReorderParams);
      } catch (err) {
        console.error(err, err && err.response ? JSON.stringify(err.response) : '');
        (this as any).$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'Failed to reorder Task.  Please contact developer support'
        });
      }
    },
    getChildPayload (index:any) {
      return (this as any).getTasksForReorder()[index]
    },
    getColumnAssignee() {
      return {assignedToAppUserId : (this as any).$store.state.user.appUserId, assignedToSecurityRoleId : null};
    },
    canReorder(sourceContainerOptions : any, payload: any) {
      const taskUserId = (this as any).$store.state.task.userIdInFocus;
      const loggedInUser = (this as any).$store.state.user.appUserId;
      if (!(this as any).acceptDrop || taskUserId !== loggedInUser) {
        return false;
      }
      if (!(this as any).maxRows) {
        return true;
      }
      const tasksForReorder = this.getTasksForReorder();
      return (this as any).maxRows > tasksForReorder.length || tasksForReorder.some((taskForReorder: any) => taskForReorder.id === payload.id);
    },
    canDrag(task: TaskModel) {
      const taskUserId = (this as any).$store.state.task.userIdInFocus;
      const loggedInUser = (this as any).$store.state.user.appUserId;
      return !task.isEditing && (this as any).acceptDrop && taskUserId === loggedInUser;
    }
  }
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/mixins/TaskColumnMixin.ts